<template>
    <canvas ref="canvas" height="160" style="width: 100%;" />
</template>

<script>
const SEASON_COLORS = {
    spring: 'rgba(74, 167, 100, 0.85)',
    summer: 'rgba(245, 185, 50, 0.85)',
    autumn: 'rgba(210, 100, 50, 0.85)',
    winter: 'rgba(90, 140, 200, 0.85)',
}

function seasonKey(name) {
    const n = name.toLowerCase()
    if (n.includes('spring') || n.includes('vernal')) return 'spring'
    if (n.includes('summer')) return 'summer'
    if (n.includes('autumn') || n.includes('fall')) return 'autumn'
    if (n.includes('winter')) return 'winter'
    return null
}

export default {
    name: 'SeasonChart',
    props: {
        seasons: { type: Array, default: () => [] },
        title:    { type: String, default: '' },
        subtitle: { type: String, default: '' },
        subtitle2:{ type: String, default: '' },
    },
    watch: {
        seasons() { this.redraw() },
    },
    mounted() {
        this.redraw()
    },
    methods: {
        redraw() {
            const canvas = this.$refs.canvas
            if (!canvas || !this.seasons.length) return

            const dpr  = window.devicePixelRatio || 1
            const cssW = canvas.parentElement ? canvas.parentElement.clientWidth : 600
            const cssH = parseInt(canvas.style.height) || canvas.height
            canvas.width  = cssW * dpr
            canvas.height = cssH * dpr
            canvas.style.height = cssH + 'px'
            const ctx = canvas.getContext('2d')
            ctx.scale(dpr, dpr)
            const w = cssW
            const h = cssH

            const PAD_TOP    = 56
            const PAD_BOTTOM = 48
            const PAD_LEFT   = 16
            const PAD_RIGHT  = 16
            const plotW = w - PAD_LEFT - PAD_RIGHT
            const plotH = h - PAD_TOP - PAD_BOTTOM  // 56

            const BAR_H   = 32
            const barTop  = PAD_TOP + (plotH - BAR_H) / 2   // PAD_TOP + 12
            const barBot  = barTop + BAR_H

            ctx.clearRect(0, 0, w, h)

            if (this.title) {
                ctx.font = 'bold 11px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.6)'
                ctx.fillText(this.title, PAD_LEFT, 16)
            }
            if (this.subtitle) {
                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(this.subtitle, PAD_LEFT, 28)
            }
            if (this.subtitle2) {
                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(this.subtitle2, PAD_LEFT, 40)
            }

            const totalDays = this.seasons.reduce((s, e) => s + e.duration_days, 0)

            // Draw segments and collect boundary x positions for x-axis labels
            let cumDays = 0
            const boundaries = []  // { x, day } for each segment start + final end
            boundaries.push({ x: PAD_LEFT, day: 1 })

            ctx.font = 'bold 10px system-ui, sans-serif'
            for (const season of this.seasons) {
                const segW = (season.duration_days / totalDays) * plotW
                const segX = PAD_LEFT + (cumDays / totalDays) * plotW
                const key  = seasonKey(season.name)
                ctx.fillStyle = key ? SEASON_COLORS[key] : 'rgba(150,150,150,0.7)'
                ctx.fillRect(segX, barTop, segW, BAR_H)

                // Duration label centered in segment
                const label = String(Math.floor(season.duration_days))
                const textW = ctx.measureText(label).width
                if (textW < segW - 8) {
                    ctx.fillStyle = 'rgba(255,255,255,1)'
                    ctx.textAlign = 'center'
                    ctx.fillText(label, segX + segW / 2, barTop + BAR_H / 2 + 4)
                }

                cumDays += season.duration_days
                boundaries.push({ x: PAD_LEFT + (cumDays / totalDays) * plotW, day: cumDays })
            }

            // Bar border
            ctx.strokeStyle = 'rgba(0,0,0,0.15)'
            ctx.lineWidth = 1
            ctx.strokeRect(PAD_LEFT, barTop, plotW, BAR_H)

            // X-axis day labels at each boundary
            ctx.font = '10px system-ui, sans-serif'
            ctx.fillStyle = 'rgba(0,0,0,0.4)'
            for (const b of boundaries) {
                ctx.textAlign = b.x <= PAD_LEFT + 1 ? 'left' : b.x >= PAD_LEFT + plotW - 1 ? 'right' : 'center'
                ctx.fillText(Math.round(b.day), b.x, barBot + 14)
            }

            // Legend — centered horizontally
            ctx.font = '11px system-ui, sans-serif'
            const SWATCH_W = 16
            const SWATCH_H = 8
            const ITEM_GAP = 24

            // Measure total legend width first
            let totalLegendW = 0
            const items = this.seasons.map(s => {
                const label = s.name
                const labelW = ctx.measureText(label).width
                return { season: s, label, labelW, key: seasonKey(s.name) }
            })
            items.forEach((item, i) => {
                totalLegendW += SWATCH_W + 8 + item.labelW
                if (i < items.length - 1) totalLegendW += ITEM_GAP
            })

            const legendY = h - 8
            let lx = (w - totalLegendW) / 2

            for (const item of items) {
                const color = item.key ? SEASON_COLORS[item.key] : 'rgba(150,150,150,0.85)'
                ctx.fillStyle = color
                ctx.fillRect(lx, legendY - SWATCH_H, SWATCH_W, SWATCH_H)
                ctx.strokeStyle = 'rgba(0,0,0,0.15)'
                ctx.lineWidth = 1
                ctx.strokeRect(lx, legendY - SWATCH_H, SWATCH_W, SWATCH_H)
                lx += SWATCH_W + 8
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.5)'
                ctx.fillText(item.label, lx, legendY)
                lx += item.labelW + ITEM_GAP
            }
        },
    },
}
</script>
